import posthog from 'posthog-js';
import { isClientContext } from '@/lib/auth/tokenManager';

let initialized = false;

/**
 * Whether PostHog was initialized this session.
 *
 * All helpers (events, user context) must check this before calling into
 * posthog-js so local dev / missing-key builds are clean no-ops.
 */
export function isPostHogEnabled(): boolean {
  return initialized;
}

/**
 * Initialize PostHog product analytics.
 *
 * Scope: PostHog tracks the CLIENT portal experience only. Preparer-area
 * activity is already covered by Azure logs; errors are covered by Sentry.
 * Every event (including session-replay snapshots) passes through
 * `before_send`, which drops anything captured outside the client area.
 *
 * Privacy: this app handles tax data (SSNs, tax returns), so all input
 * values and rendered text are masked in autocapture and session replay.
 * We track WHAT users do (clicks, navigation, funnels, dead/rage clicks),
 * never the data on screen.
 *
 * Enabled in production builds with a key, like Sentry. For local testing,
 * set VITE_POSTHOG_KEY and VITE_POSTHOG_DEBUG=true in .env.local.
 */
export function initPostHog(): void {
  const key = import.meta.env.VITE_POSTHOG_KEY;
  const debugMode = import.meta.env.VITE_POSTHOG_DEBUG === 'true';

  if (!key || (!import.meta.env.PROD && !debugMode)) {
    return;
  }

  posthog.init(key, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',

    // SPA-aware defaults: captures $pageview on TanStack Router history
    // changes (not just full page loads) and $pageleave for bounce/duration.
    defaults: '2025-05-24',

    // Only create person profiles for logged-in clients we identify();
    // anonymous visitors (login pages) stay lightweight and unprofiled.
    person_profiles: 'identified_only',

    // Autocapture clicks/interactions, but never element text or attribute
    // values — client names, SSNs, and dollar amounts render as text in
    // this app. Dead-click and rage-click detection still work on the
    // masked elements.
    autocapture: true,
    mask_all_text: true,
    mask_all_element_attributes: true,

    // Core Web Vitals (LCP/CLS/INP/FCP) per route — real-user perf signal
    // that neither Sentry's trace sampling nor Azure logs give us.
    capture_performance: { web_vitals: true },

    // Sentry owns exceptions; don't double-report.
    capture_exceptions: false,

    // Session replay is started explicitly (startSessionRecording) once an
    // authenticated CLIENT is identified — see PostHogUserContext. Replays
    // record layout and interaction only: all inputs and all text masked.
    disable_session_recording: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
    },

    // Hard gate: drop every event captured outside the client area
    // (preparer routes). Defense in depth on top of the identify/recording
    // gating in PostHogUserContext.
    before_send: (event) => {
      if (!isClientContext()) {
        return null;
      }
      return event;
    },

    debug: debugMode,
  });

  initialized = true;
}

export { posthog };
